/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiSettings, FiLink, FiUser, FiSave } from "react-icons/fi";
import WhatsLayout from "../layouts/WhatsLayout";
import { useUI } from "../theme/ThemeProvider";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";

// Using colors from useUI instead of hardcoded COLORS

export default function AdminStoreSettings() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  console.log(tenantId);

  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentTenantData, setCurrentTenantData] = useState(null);

  const [general, setGeneral] = useState({
    storeName: "",
    storeSlug: "",
  });
  const storeUrl = React.useMemo(() => {
    if (!general.storeSlug) return "";
    return `https://${general.storeSlug}.madar.iq/`;
  }, [general.storeSlug]);

  const [social, setSocial] = useState({
    facebook: "",
    instagram: "",
    snapchat: "",
    tiktok: "",
    whatsapp: "",
  });

  const [policies, setPolicies] = useState({
    privacy: "",
    delivery: "",
    refund: "",
  });

  // Fetch tenant data on component mount
  useEffect(() => {
    fetchTenantData();
  }, []);

  const resolveCurrentTenant = (data) => {
    const list = Array.isArray(data) ? data : data ? [data] : [];
    if (!list.length) return null;

    const matched = list.find(
      (t) => Number(t.TenantId ?? t.tenantId) === Number(tenantId)
    );
    return matched || list[0];
  };

  const parsePaymentMethods = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string" && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const fetchTenantData = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("authToken");

      const res = await fetch(
        `${baseUrl}/GetAllTenantFullDetails?tenantId=${tenantId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      if (!res.ok) {
        throw new Error("Failed to fetch tenant data");
      }

      const data = await res.json();
      const tenant = resolveCurrentTenant(data);

      if (!tenant) return;

      const paymentMethods = parsePaymentMethods(
        tenant.PaymentMethods ?? tenant.paymentMethods
      );

      // general
      setGeneral({
        storeName:
          tenant.ClientName ||
          tenant.clientName ||
          "",
        storeSlug: tenant.storeSlug || tenant.StoreSlug || "",
      });

      // social
      setSocial({
        facebook: tenant.facebook || tenant.Facebook || "",
        instagram: tenant.instagram || tenant.Instagram || "",
        snapchat: tenant.snapchat || tenant.Snapchat || "",
        tiktok: tenant.tiktok || tenant.Tiktok || "",
        whatsapp: tenant.whatsapp || tenant.Whatsapp || "",
      });

      // policies
      setPolicies({
        privacy: tenant.privacyPolicy || tenant.PrivacyPolicy || "",
        delivery: tenant.deliveryPolicy || tenant.DeliveryPolicy || "",
        refund: tenant.refundPolicy || tenant.RefundPolicy || "",
      });

      setCurrentTenantData({
        ...tenant,
        PaymentMethods: paymentMethods,
      });
    } catch (err) {
      console.error("❌ Error fetching tenant data:", err);
    } finally {
      setLoading(false);
    }
  };


  const handleSave = async () => {
    if (!perms.editStoreSettings) return;

    if (!currentTenantData || tenantId <= 0) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "خطأ" : "Error",
        text: isArabic ? "بيانات المتجر غير موجودة" : "Tenant data not found",
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
        confirmButtonColor: colors.mainColor,
      });
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem("authToken");
      const slug = (general.storeSlug || "").trim();

      // Validate Slug Uniqueness
      if (slug) {
        try {
          const validationRes = await fetch(`${baseUrl}/GetAllTenantFullDetails?tenantId=-1`, {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: token ? `Bearer ${token}` : "",
            },
          });
          if (validationRes.ok) {
            const allTenants = await validationRes.json();
            const list = Array.isArray(allTenants) ? allTenants : [];
            const existing = list.find((t) =>
              Number(t.TenantId ?? t.tenantId) !== Number(tenantId) &&
              (
                (t.storeSlug && t.storeSlug.toLowerCase() === slug.toLowerCase()) ||
                (t.StoreSlug && t.StoreSlug.toLowerCase() === slug.toLowerCase())
              )
            );
            if (existing) {
              setSaving(false);
              Swal.fire({
                icon: "error",
                title: isArabic ? "اسم مستخدم" : "Name Used",
                text: isArabic
                  ? "اسم الرابط (Slug) مستخدم مسبقاً، يرجى اختيار اسم آخر"
                  : "This store slug is already used by another store, please choose another one",
                background: isDark ? "#1E293B" : "#fff",
                color: isDark ? "#fff" : "#000",
                confirmButtonColor: colors.mainColor,
              });
              return;
            }
          }
        } catch (err) {
          console.error("Failed to validate slug", err);
        }
      }

      const tenant = currentTenantData;
      const formData = new FormData();

      // الحفاظ على بيانات المتجر الحالي — نحدّث فقط الرابط/السوشيال/السياسات
      formData.append("TenantId", tenantId);
      formData.append("TenantName", tenant.TenantName || tenant.tenantName || general.storeName || "");
      formData.append("EngTenantName", tenant.EngTenantName || tenant.engTenantName || "");
      formData.append("Email", tenant.Email || tenant.email || "");
      formData.append("Phone", tenant.Phone || tenant.phone || "");
      formData.append("SubDomain", tenant.SubDomain || tenant.subDomain || "");
      formData.append("IsActive", tenant.IsActive ?? tenant.isActive ?? 1);
      formData.append("CreatedAt", tenant.CreatedAt || tenant.createdAt || new Date().toISOString());
      formData.append("Product", tenant.Product || tenant.product || "");
      formData.append("MainPlanId", tenant.MainPlanId ?? tenant.mainPlanId ?? 0);
      formData.append("TenantDtlId", tenant.TenantDtlId ?? tenant.tenantDtlId ?? 0);
      formData.append("LogoFile", "");

      formData.append("ClientName", tenant.ClientName || tenant.clientName || general.storeName || "");
      formData.append("SectorType", tenant.SectorType || tenant.sectorType || "");
      formData.append("BusinessName", tenant.BusinessName || tenant.businessName || "");
      formData.append("CompanyName", tenant.CompanyName || tenant.companyName || "");
      formData.append("ThemeId", tenant.ThemeId ?? tenant.themeId ?? 0);
      formData.append("DetailPlanId", tenant.DetailPlanId ?? tenant.detailPlanId ?? 0);
      formData.append("ColorName", tenant.ColorName || tenant.colorName || "");
      formData.append("PlanType", tenant.PlanType || tenant.planType || "");
      formData.append("EngPlanType", tenant.EngPlanType || tenant.engPlanType || "");
      formData.append("Price", tenant.Price ?? tenant.price ?? 0);
      formData.append("Descripsion", tenant.Descripsion || tenant.descripsion || "");
      formData.append("EngDescripsion", tenant.EngDescripsion || tenant.engDescripsion || "");

      for (let i = 1; i <= 8; i++) {
        formData.append(`Note${i}`, tenant[`Note${i}`] || tenant[`note${i}`] || "");
      }

      // PaymentMethods يأتي من الـ API كنص JSON أحياناً — نرسله كنص JSON دائماً
      const paymentMethods = parsePaymentMethods(tenant.PaymentMethods ?? tenant.paymentMethods);
      formData.append("PaymentMethods", JSON.stringify(paymentMethods));

      // Social
      formData.append("Facebook", (social.facebook || "").trim());
      formData.append("Instagram", (social.instagram || "").trim());
      formData.append("Snapchat", (social.snapchat || "").trim());
      formData.append("Tiktok", (social.tiktok || "").trim());
      formData.append("Whatsapp", (social.whatsapp || "").trim());

      // Policies
      formData.append("PrivacyPolicy", (policies.privacy || "").trim());
      formData.append("DeliveryPolicy", (policies.delivery || "").trim());
      formData.append("RefundPolicy", (policies.refund || "").trim());

      // Slug
      formData.append("StoreSlug", slug);

      const res = await fetch(`${baseUrl}/UpdateTenantDetails/UpdateTenantFullDetails`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      const rawText = await res.text();
      let result = null;
      if (rawText) {
        try {
          result = JSON.parse(rawText);
        } catch {
          result = { message: rawText };
        }
      }

      if (!res.ok) {
        throw new Error(result?.message || result?.Message || "Failed to update tenant");
      }

      await fetchTenantData();

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم التحديث بنجاح" : "Updated Successfully",
        timer: 2000,
        showConfirmButton: false,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    } catch (err) {
      console.error("❌ Update Error:", err);
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل التحديث" : "Update Failed",
        text: err.message,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
        confirmButtonColor: colors.mainColor,
      });
    } finally {
      setSaving(false);
    }
  };


  if (!perms.viewStoreSettings) {
    return <NoAccess />;
  }
  return (
    <WhatsLayout title={isArabic ? "إعدادات المتجر" : "Store Settings"}>
      <section
        dir={isArabic ? "rtl" : "ltr"}
        className={`min-h-screen px-5 sm:px-10 py-10 transition-all duration-300 ${isDark ? "text-gray-100" : "text-gray-900"
          }`}
        style={{
          background: isDark
            ? "linear-gradient(180deg, #0B1020 0%, #131827 100%)"
            : "linear-gradient(180deg, #F8FAFC 0%, #E2E8F0 100%)",
        }}
      >


        {/* ===== تبويبات التنقل ===== */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {[
            perms.viewStoreSettings && { key: "general", icon: <FiUser />, label: isArabic ? "عام" : "General" },
            perms.viewStoreSettings && { key: "social", icon: <FiLink />, label: isArabic ? "التواصل الاجتماعي" : "Social Links" },
            perms.viewStoreSettings && { key: "policies", icon: <FiSettings />, label: isArabic ? "السياسات" : "Policies" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-2 rounded-xl font-semibold transition-all shadow-md ${activeTab === tab.key
                ? "text-white scale-[1.05]"
                : isDark
                  ? "text-gray-300"
                  : "text-gray-600"
                }`}
              style={{
                background: activeTab === tab.key
                  ? colors.mainColor
                  : isDark
                    ? "#1E293B"
                    : "#ffffff",
                border: isDark
                  ? "1px solid rgba(255,255,255,0.25)"
                  : "1px solid rgba(0,0,0,0.1)",
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#7400BF]"></div>
              <p className="mt-4" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                {isArabic ? "جاري التحميل..." : "Loading..."}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* ===== محتوى التبويبات ===== */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="rounded-2xl shadow-lg border p-6"
              style={{
                backgroundColor: isDark ? "#1E293B" : "#ffffff",
                border: isDark
                  ? "1px solid rgba(255,255,255,0.25)"
                  : "1px solid rgba(0,0,0,0.1)",
              }}
            >
              {/* تبويب عام */}
              {activeTab === "general" && perms.editStoreSettings && (
                <div>
                  <h3
                    className="text-xl font-bold mb-5"
                    style={{ color: colors.mainColor }}
                  >
                    {isArabic ? "الإعدادات العامة" : "General Settings"}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        className="block mb-2 font-semibold"
                        style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}
                      >
                        {isArabic ? "اسم المتجر" : "Store Name"}
                      </label>
                      <input
                        type="text"
                        value={general.storeName}
                        readOnly
                        className="w-full rounded-lg px-3 py-2 focus:outline-none transition-colors cursor-default"
                        style={{
                          backgroundColor: isDark ? "#0B1220" : "#EEF2F7",
                          border: isDark
                            ? "1px solid rgba(255,255,255,0.18)"
                            : "1px solid rgba(0,0,0,0.08)",
                          color: isDark ? "#94a3b8" : "#64748b",
                        }}
                        title={isArabic ? "اسم المتجر للقراءة فقط" : "Store name is read-only"}
                      />
                    </div>
                    <div>
                      <label
                        className="block mb-2 font-semibold"
                        style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}
                      >
                        {isArabic ? "الاسم في الرابط" : "Store Slug"}
                      </label>
                      <input
                        type="text"
                        value={general.storeSlug}
                        onChange={(e) => {
                          const value = e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9-]/g, "")
                            .replace(/--+/g, "-");
                          setGeneral((prev) => ({ ...prev, storeSlug: value }));
                        }}
                        className="w-full rounded-lg px-3 py-2 focus:outline-none focus:border-purple-400 transition-colors"
                        style={{
                          backgroundColor: isDark ? "#0F172A" : "#F8FAFC",
                          border: isDark
                            ? "1px solid rgba(255,255,255,0.25)"
                            : "1px solid rgba(0,0,0,0.1)",
                          color: isDark ? "#f1f5f9" : "#1e293b",
                        }}
                      />
                      {storeUrl && (
                        <div className="mt-3 flex items-center justify-between gap-3 p-3 rounded-lg text-sm"
                          style={{
                            background: isDark ? "#0F172A" : "#F1F5F9",
                            border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid #CBD5E1",
                            color: isDark ? "#E5E7EB" : "#1E293B"
                          }}>

                          <div className="truncate">
                            <span className="opacity-70">{isArabic ? "رابط المتجر:" : "Store URL:"}</span><br />
                            <a href={storeUrl} target="_blank" rel="noreferrer"
                              className="font-semibold text-purple-400 ">
                              {storeUrl}
                            </a>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(storeUrl);
                                Swal.fire({
                                  icon: "success",
                                  title: isArabic ? "تم النسخ" : "Copied",
                                  timer: 1500,
                                  showConfirmButton: false,
                                });
                              }}
                              className="p-2 rounded-md transition"
                              style={{ background: colors.mainColor }}
                            >
                              <FiLink className="text-white" />
                            </button>
                          </div>
                        </div>
                      )}

                    </div>
                  </div>

                  <div className="flex justify-center sm:justify-end">
                    <button
                      onClick={perms.editStoreSettings ? handleSave : null}
                      disabled={saving}
                      className="mt-6 flex items-center gap-2 px-6 py-2 rounded-lg text-white font-semibold shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: saving ? "#94a3b8" : colors.mainColor }}
                    >
                      {saving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          {isArabic ? "جاري الحفظ..." : "Saving..."}
                        </>
                      ) : (
                        <>
                          <FiSave /> {isArabic ? "حفظ التعديلات" : "Save Changes"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* تبويب التواصل الاجتماعي */}
              {activeTab === "social" && perms.editStoreSettings && (
                <div>
                  <h3
                    className="text-xl font-bold mb-5"
                    style={{ color: colors.mainColor }}
                  >
                    {isArabic ? "وسائل التواصل الاجتماعي" : "Social Media Links"}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {Object.keys(social).map((key) => (
                      <div key={key}>
                        <label
                          className="block mb-2 font-semibold capitalize"
                          style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}
                        >
                          {isArabic
                            ? {
                              facebook: "رابط الفيسبوك",
                              instagram: "رابط الانستغرام",
                              snapchat: "رابط السناب شات",
                              tiktok: "رابط التيك توك",
                              whatsapp: "رابط الواتساب",
                            }[key]
                            : `${key.charAt(0).toUpperCase() + key.slice(1)} Link`}
                        </label>
                        <input
                          type="text"
                          value={social[key]}
                          onChange={(e) =>
                            setSocial((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                          className="w-full rounded-lg px-3 py-2 focus:outline-none focus:border-purple-400 transition-colors"
                          style={{
                            backgroundColor: isDark ? "#0F172A" : "#F8FAFC",
                            border: isDark
                              ? "1px solid rgba(255,255,255,0.25)"
                              : "1px solid rgba(0,0,0,0.1)",
                            color: isDark ? "#f1f5f9" : "#1e293b",
                          }}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-center sm:justify-end">
                    <button
                      onClick={perms.editStoreSettings ? handleSave : null}
                      disabled={saving}
                      className="mt-6 flex items-center gap-2 px-6 py-2 rounded-lg text-white font-semibold shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: saving ? "#94a3b8" : colors.mainColor }}
                    >
                      {saving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          {isArabic ? "جاري الحفظ..." : "Saving..."}
                        </>
                      ) : (
                        <>
                          <FiSave /> {isArabic ? "حفظ التعديلات" : "Save Changes"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* تبويب السياسات */}
              {activeTab === "policies" && perms.editStoreSettings && (
                <div>
                  <h3
                    className="text-xl font-bold mb-5"
                    style={{ color: colors.mainColor }}
                  >
                    {isArabic ? "سياسات المتجر" : "Store Policies"}
                  </h3>
                  <div className="grid grid-cols-1 gap-5">
                    {Object.keys(policies).map((key) => (
                      <div key={key}>
                        <label
                          className="block mb-2 font-semibold"
                          style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}
                        >
                          {isArabic
                            ? {
                              privacy: "سياسة الخصوصية",
                              delivery: "سياسة التوصيل",
                              refund: "سياسة الاسترجاع",
                            }[key]
                            : `${key.charAt(0).toUpperCase() + key.slice(1)} Policy`}
                        </label>
                        <textarea
                          rows="4"
                          value={policies[key]}
                          onChange={(e) =>
                            setPolicies((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                          className="w-full rounded-lg px-3 py-2 focus:outline-none focus:border-purple-400 transition-colors resize-none"
                          style={{
                            backgroundColor: isDark ? "#0F172A" : "#F8FAFC",
                            border: isDark
                              ? "1px solid rgba(255,255,255,0.25)"
                              : "1px solid rgba(0,0,0,0.1)",
                            color: isDark ? "#f1f5f9" : "#1e293b",
                          }}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-center sm:justify-end">
                    <button
                      onClick={perms.editStoreSettings ? handleSave : null}
                      disabled={saving}
                      className="mt-6 flex items-center gap-2 px-6 py-2 rounded-lg text-white font-semibold shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: saving ? "#94a3b8" : colors.mainColor }}
                    >
                      {saving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          {isArabic ? "جاري الحفظ..." : "Saving..."}
                        </>
                      ) : (
                        <>
                          <FiSave /> {isArabic ? "حفظ التعديلات" : "Save Changes"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}
      </section>
    </WhatsLayout>
  );
}
